import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subscription, of, forkJoin } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { AdhocReportsService } from "../../../services/adhoc-reports-service";
import { InsuranceDataListItem } from "../../../models/adhoc-reports.model";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { CheckboxRendererComponent } from '../../../../common/components/ag-grid/checkbox-renderer.component';
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../../common/components/ag-grid/filters/boolean-filter.component";

@Component({
    selector: 'app-insurance-full-data-list',
    templateUrl: './insurance-full-data-list.component.html',
    styleUrls: ['./insurance-full-data-list.component.scss'],
    standalone: false
})
export class InsuranceFullDataListComponent implements OnInit, OnDestroy {

    _booleanfilterParams = { isInServerSideFilterMode: false, } as IBooleanFilterParams;

    data: Observable<InsuranceDataListItem[]>;

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }


	gridOptions: GridOptions;
	gridApi?: GridApi;


    subheader: SubHeader = new SubHeader({ title: "Insurance - Full Data List" });
    subscription: Subscription = new Subscription();


    constructor(private _reportService: AdhocReportsService, private _dialogService: DynamicDialogService, private _subHeaderService: SubHeaderService, private formatter: GridFormatterService, private domainService: DomainDefinitionService) {
      
    }

    ngOnInit() {
        this._subHeaderService.updateSubHeader(this.subheader);
        this.gridOptions = this.defineGridOptions();
        let domain$ = this.domainService.getValues("INS_TYPE");
        let data$ = this._reportService.getInsuranceDataListReport();
        this.data = forkJoin([data$, domain$]).pipe(
            catchError((error) => {
                console.error(error);
                this._dialogService.showError();
                return of([null, null]);
            }),
            map(([data, domains]) => {
                if (data != null && domains != null) {                    
                    data.forEach(i => {
                        let domain = domains.find(d => d.Code.toLocaleLowerCase() === i.insurancePolicyType.toLocaleLowerCase() || d.Code.toLocaleLowerCase().includes(i.insurancePolicyType.toLocaleLowerCase()));
                        if (domain != null) {
                            i.insurancePolicyType = domain.Definition;
                            i.insurancePolicyType = i.insurancePolicyType.replace("Liability", "");
                            i.insurancePolicyType = i.insurancePolicyType.replace("(each occurrence)", "");
                            i.insurancePolicyType = i.insurancePolicyType.trim();
                        }
                    })
                } else {
                    return [];
                }
                return data;
            })
        );
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    defineColumns(): ColDef[] {
        let defs: ColDef[] = [
            {
                headerClass: "wrapped",
                headerName: "RICS Organization #",
                headerTooltip: "RICS Organization #",
                field: "ricsOrganizationId",
                tooltipField: "ricsOrganizationId",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,              
                minWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "RBDMS Owner #",
                headerTooltip: "RBDMS Owner #",
                field: "rbdmsOwnerNumber",
                tooltipField: "rbdmsOwnerNumber",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Owner Name",
                headerTooltip: "Owner Name",
                field: "ownerName",
                tooltipField: "ownerName",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Owner Type",
                headerTooltip: "Owner Type",
                valueGetter: this.formatOwnerType(),
                tooltipValueGetter: this.formatOwnerType(),               
                minWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Insurance Policy Type",
                headerTooltip: "Insurance Policy Type",
                field: "insurancePolicyType",
                tooltipField: "insurancePolicyType",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Policy #",
                headerTooltip: "Policy #",
                field: "policyNumber",
                tooltipField: "policyNumber",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 125,
                cellClass: "stringType"
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Limit",
                headerTooltip: "Limit",
                field: "limit",
                valueFormatter: this.formatter.formatCurrency("limit"),
                tooltipValueGetter: this.formatter.formatCurrency("limit"),
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Effective Date",
                headerTooltip: "Effective Date",
                field: "effectiveDate",
                tooltipField: "effectiveDate",
                valueFormatter: this.formatter.formatDate("effectiveDate", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("effectiveDate", "MM/dd/yyyy", "N/A"),
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Expiration Date",
                headerTooltip: "Expiration Date",
                field: "expirationDate",
                tooltipField: "expirationDate",
                valueFormatter: this.formatter.formatDate("expirationDate", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("expirationDate", "MM/dd/yyyy", "N/A"),
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Release Date",
                headerTooltip: "Release Date",
                field: "releaseDate",
                tooltipField: "releaseDate",
                valueFormatter: this.formatter.formatDate("releaseDate", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("releaseDate", "MM/dd/yyyy", "N/A"),
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Environmental Endorsement",
                headerTooltip: "Environmental Endorsement",
                field: "environmentalEndorsement",
                tooltipField: "environmentalEndorsement",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "environmentalEndorsement",
                    description: "Environmental Endorsement",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("environmentalEndorsement"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Is Active",
                headerTooltip: "Is Active",
                field: "isActive",
                tooltipField: "isActive",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "isActive",
                    description: "Is Active",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("isActive"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                maxWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Surplus Insurance",
                headerTooltip: "Surplus Insurance",
                field: "surplusInsurance",
                tooltipField: "surplusInsurance",
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "surplusInsurance",
                    description: "Surplus Insurance",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("surplusInsurance"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Replacement Approval Date",
                headerTooltip: "Replacement Approval Date",
                field: "replacementApprovalDate",
                tooltipField: "replacementApprovalDate",
                valueFormatter: this.formatter.formatDate("replacementApprovalDate", "MM/dd/yyyy", "N/A"),
                tooltipValueGetter: this.formatter.formatDate("replacementApprovalDate", "MM/dd/yyyy", "N/A"),
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Is Compliant",
                headerTooltip: "Organization Is Compliant",
                field: "compliance.isInsuranceCompliant",
                tooltipField: "compliance.isInsuranceCompliant",
                valueFormatter: this.formatter.formatBooleanToYesNo("compliance.isInsuranceCompliant"),
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("compliance.isInsuranceCompliant"),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Insurer",
                headerTooltip: "Insurer",
                field: "insurer",
                tooltipField: "insurer",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 140
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Agency",
                headerTooltip: "Agency",
                field: "agency",
                tooltipField: "agency",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Agent",
                headerTooltip: "Agent",
                field: "agent",
                tooltipField: "agent",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Agent E-mail",
                headerTooltip: "Agent E-mail",
                field: "agentEmail",
                tooltipField: "agentEmail",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Agent Phone",
                headerTooltip: "Agent Phone",
                field: "agentPhone",
                tooltipField: "agentPhone",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Primary Contact",
                headerTooltip: "Organization Primary Contact",
                field: "organizationPrimaryContact",
                tooltipField: "organizationPrimaryContact",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Address 1",
                headerTooltip: "Organization Address 1",
                field: "organizationAddress.street",
                tooltipField: "organizationAddress.street",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Address 2",
                headerTooltip: "Organization Address 2",
                field: "organizationAddress.suite",
                tooltipField: "organizationAddress.suite",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization City",
                headerTooltip: "Organization City",
                field: "organizationAddress.city",
                tooltipField: "organizationAddress.city",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization State",
                headerTooltip: "Organization State",
                field: "organizationAddress.stateCode",
                tooltipField: "organizationAddress.stateCode",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Zip Code",
                headerTooltip: "Organization Zip Code",
                field: "organizationAddress.zipCode",
                tooltipField: "organizationAddress.zipCode",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization Phone",
                headerTooltip: "Organization Phone",
                field: "organizationPhone",
                tooltipField: "organizationPhone",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Organization E-mail",
                headerTooltip: "Organization E-mail",
                field: "organizationEmail",
                tooltipField: "organizationEmail",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
        ];
        return defs;
    }

    defineGridOptions(): GridOptions {
        let options = this.formatter.BuildDefaultGridOptionsWithColumns(this.defineColumns(), false, true, true, true);
		options.groupDisplayType = "groupRows";
        options.headerHeight = 50;
		options.pagination = false;
		options.onGridReady = (params) => { this.gridApi = params.api; };
        return options;
    }

    formatOwnerType(): any {
        return (obj: any) => {
            let ownership: string = "";
            if (obj.data && obj.data.compliance) {
                if (obj.data.compliance.isBrineHauler) ownership = "Brine Hauler";
                if (obj.data.compliance.isWellOwner) {
                    if (ownership.length > 0) ownership = `${ownership}, Well Owner`;
                    else ownership = "Well Owner";
                }
            }
            if (ownership.length === 0) ownership = "N/A";
            return ownership;
        }
    }
}
